import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import {
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  EllipsisVerticalIcon,
  UserCircleIcon,
  ArrowLeftIcon,
  PhoneIcon,
  VideoCameraIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckIcon,
  CheckCircleIcon
} from '@heroicons/react/24/solid';

interface User {
  id: string;
  name: string;
  avatar?: string;
  userType: 'teacher' | 'student' | 'parent';
  isOnline: boolean;
  lastSeen?: string;
}

interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  timestamp: string;
  isRead: boolean;
  messageType: 'text' | 'lesson_request' | 'system';
  attachments?: {
    type: 'image' | 'document';
    url: string;
    name: string;
  }[];
}

interface Conversation {
  id: string;
  participants: User[];
  lastMessage: Message;
  unreadCount: number;
  isPinned: boolean;
  lessonContext?: {
    instrument: string;
    status: 'pending' | 'confirmed' | 'completed';
  };
}

// Mock current user data
const currentUser: User = {
  id: 'teacher-1',
  name: 'Michael Rodriguez',
  userType: 'teacher',
  isOnline: true
};

// Mock conversations data
const mockConversations: Conversation[] = [
  {
    id: 'conv-1',
    participants: [
      currentUser,
      {
        id: 'student-1',
        name: 'Emma Thompson',
        userType: 'student',
        isOnline: true
      }
    ],
    lastMessage: {
      id: 'msg-1',
      senderId: 'student-1',
      receiverId: 'teacher-1',
      content: 'Thank you for accepting my lesson request! When would be a good time to start?',
      timestamp: '2024-01-15T14:30:00Z',
      isRead: false,
      messageType: 'text'
    },
    unreadCount: 2,
    isPinned: false,
    lessonContext: {
      instrument: 'Piano',
      status: 'confirmed'
    }
  },
  {
    id: 'conv-2',
    participants: [
      currentUser,
      {
        id: 'parent-1',
        name: 'Sarah Kim (Parent)',
        userType: 'parent',
        isOnline: false,
        lastSeen: '2 hours ago'
      }
    ],
    lastMessage: {
      id: 'msg-2',
      senderId: 'teacher-1',
      receiverId: 'parent-1',
      content: 'Your daughter did great in today\'s lesson! She\'s making excellent progress with scales.',
      timestamp: '2024-01-15T12:45:00Z',
      isRead: true,
      messageType: 'text'
    },
    unreadCount: 0,
    isPinned: true,
    lessonContext: {
      instrument: 'Voice/Vocals',
      status: 'completed'
    }
  },
  {
    id: 'conv-3',
    participants: [
      currentUser,
      {
        id: 'student-2',
        name: 'Marcus Chen',
        userType: 'student',
        isOnline: false,
        lastSeen: '1 day ago'
      }
    ],
    lastMessage: {
      id: 'msg-3',
      senderId: 'student-2',
      receiverId: 'teacher-1',
      content: 'Hi! I\'d like to schedule a piano lesson focusing on jazz improvisation.',
      timestamp: '2024-01-14T18:20:00Z',
      isRead: true,
      messageType: 'lesson_request'
    },
    unreadCount: 0,
    isPinned: false,
    lessonContext: {
      instrument: 'Piano',
      status: 'pending'
    }
  }
];

// Mock messages for a conversation
const mockMessages: Message[] = [
  {
    id: 'msg-1-1',
    senderId: 'student-1',
    receiverId: 'teacher-1',
    content: 'Hi Mr. Rodriguez! I\'m excited to start piano lessons with you.',
    timestamp: '2024-01-15T14:00:00Z',
    isRead: true,
    messageType: 'text'
  },
  {
    id: 'msg-1-2',
    senderId: 'teacher-1',
    receiverId: 'student-1',
    content: 'Hello Emma! I\'m excited to work with you too. I saw your lesson request for beginner piano.',
    timestamp: '2024-01-15T14:05:00Z',
    isRead: true,
    messageType: 'text'
  },
  {
    id: 'msg-1-3',
    senderId: 'student-1',
    receiverId: 'teacher-1',
    content: 'Yes! I\'ve always wanted to learn classical pieces. Do you think we could start with something simple?',
    timestamp: '2024-01-15T14:10:00Z',
    isRead: true,
    messageType: 'text'
  },
  {
    id: 'msg-1-4',
    senderId: 'teacher-1',
    receiverId: 'student-1',
    content: 'Absolutely! I think we should start with basic finger exercises and maybe a simple piece like "Ode to Joy". This will help you build proper technique.',
    timestamp: '2024-01-15T14:15:00Z',
    isRead: true,
    messageType: 'text'
  },
  {
    id: 'msg-1-5',
    senderId: 'student-1',
    receiverId: 'teacher-1',
    content: 'Thank you for accepting my lesson request! When would be a good time to start?',
    timestamp: '2024-01-15T14:30:00Z',
    isRead: false,
    messageType: 'text'
  }
];

const MessagingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState<Conversation[]>(mockConversations);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>(mockMessages);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  // Log messaging page access
  console.log('Messaging page loaded for user:', currentUser);

  // Check if opened with specific conversation context
  const { openConversationWith, userType } = location.state || {};
  
  useEffect(() => {
    if (openConversationWith && !selectedConversation) {
      // Find or create conversation with specific user
      const existingConv = conversations.find(conv => 
        conv.participants.some(p => p.name === openConversationWith)
      );
      
      if (existingConv) {
        setSelectedConversation(existingConv.id);
        console.log('Opened existing conversation with:', openConversationWith);
      } else {
        // TODO: Create new conversation
        console.log('Would create new conversation with:', openConversationWith);
      }
    }
  }, [openConversationWith, conversations, selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setShowConversationList(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participants.some(p => 
      p.id !== currentUser.id && 
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const selectedConv = conversations.find(conv => conv.id === selectedConversation);
  const otherParticipant = selectedConv?.participants.find(p => p.id !== currentUser.id);

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const message: Message = {
      id: `msg-${Date.now()}`,
      senderId: currentUser.id,
      receiverId: otherParticipant?.id || '',
      content: newMessage.trim(),
      timestamp: new Date().toISOString(),
      isRead: false,
      messageType: 'text'
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');
    
    // Update conversation's last message
    setConversations(prev =>
      prev.map(conv =>
        conv.id === selectedConversation
          ? { ...conv, lastMessage: message }
          : conv
      )
    );

    console.log('Message sent:', message);
    messageInputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const markAsRead = (conversationId: string) => {
    setConversations(prev =>
      prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, unreadCount: 0 }
          : conv
      )
    );
  };

  const selectConversation = (conversationId: string) => {
    setSelectedConversation(conversationId);
    markAsRead(conversationId);
    
    if (isMobileView) {
      setShowConversationList(false);
    }
    
    console.log('Selected conversation:', conversationId);
  };

  const goBackToList = () => {
    setShowConversationList(true);
    setSelectedConversation(null);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getStatusIcon = (message: Message) => {
    if (message.senderId !== currentUser.id) return null;
    
    if (message.isRead) {
      return <CheckCircleIcon className="w-4 h-4 text-blue-500" />;
    } else {
      return <CheckIcon className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <MinimalLayout noPadding>
      <div className="h-screen bg-gray-50 flex">
        
        {/* Conversations Sidebar */}
        <div className={`${
          isMobileView ? (showConversationList ? 'w-full' : 'hidden') : 'w-80'
        } bg-white border-r border-gray-200 flex flex-col`}>
          
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center">
                <UserCircleIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No conversations found</p>
              </div>
            ) : (
              <div className="space-y-0">
                {filteredConversations.map((conversation) => {
                  const participant = conversation.participants.find(p => p.id !== currentUser.id);
                  return (
                    <button
                      key={conversation.id}
                      onClick={() => selectConversation(conversation.id)}
                      className={`w-full p-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        selectedConversation === conversation.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="relative flex-shrink-0">
                          {participant?.avatar ? (
                            <img
                              src={participant.avatar}
                              alt={participant.name}
                              className="w-12 h-12 rounded-full"
                            />
                          ) : (
                            <UserCircleIcon className="w-12 h-12 text-gray-400" />
                          )}
                          {participant?.isOnline && (
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-gray-900 truncate">
                              {participant?.name}
                            </p>
                            <div className="flex items-center space-x-2">
                              {conversation.isPinned && (
                                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                              )}
                              <span className="text-xs text-gray-500">
                                {formatTime(conversation.lastMessage.timestamp)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600 truncate flex-1 mr-2">
                              {conversation.lastMessage.messageType === 'lesson_request' ? (
                                <span className="flex items-center">
                                  <ClockIcon className="w-4 h-4 mr-1" />
                                  Lesson request
                                </span>
                              ) : (
                                conversation.lastMessage.content
                              )}
                            </p>
                            {conversation.unreadCount > 0 && (
                              <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                                {conversation.unreadCount}
                              </span>
                            )}
                          </div>
                          
                          {conversation.lessonContext && (
                            <div className="mt-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                conversation.lessonContext.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                conversation.lessonContext.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {conversation.lessonContext.instrument} • {conversation.lessonContext.status}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${
          isMobileView && showConversationList ? 'hidden' : ''
        }`}>
          {selectedConversation && selectedConv && otherParticipant ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {isMobileView && (
                      <button
                        onClick={goBackToList}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors mr-2"
                      >
                        <ArrowLeftIcon className="w-5 h-5" />
                      </button>
                    )}
                    
                    <div className="relative flex-shrink-0">
                      {otherParticipant.avatar ? (
                        <img
                          src={otherParticipant.avatar}
                          alt={otherParticipant.name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <UserCircleIcon className="w-10 h-10 text-gray-400" />
                      )}
                      {otherParticipant.isOnline && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    
                    <div>
                      <h2 className="font-semibold text-gray-900">{otherParticipant.name}</h2>
                      <p className="text-sm text-gray-500">
                        {otherParticipant.isOnline ? 'Online' : `Last seen ${otherParticipant.lastSeen}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                      <PhoneIcon className="w-5 h-5" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                      <VideoCameraIcon className="w-5 h-5" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                      <EllipsisVerticalIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => {
                  const isOwnMessage = message.senderId === currentUser.id;
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        isOwnMessage
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}>
                        <p className="text-sm">{message.content}</p>
                        <div className={`flex items-center justify-between mt-1 ${
                          isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          <span className="text-xs">
                            {formatTime(message.timestamp)}
                          </span>
                          {getStatusIcon(message)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="bg-white border-t border-gray-200 p-4">
                <div className="flex items-end space-x-3">
                  <div className="flex-1">
                    <textarea
                      ref={messageInputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type a message..."
                      rows={1}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    className="p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <PaperAirplaneIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* No Conversation Selected */
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <UserCircleIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No conversation selected</h3>
                <p className="text-gray-500">Choose a conversation from the sidebar to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </MinimalLayout>
  );
};

export default MessagingPage; 